import React, { useEffect, useState } from "react";
import { Camera, ImageIcon, Loader2, X, ShieldAlert, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { galleryApi, galleryApiErr } from "../lib/galleryApi";
import { MEMBER_TOKEN_KEY } from "../lib/authApi";
import { Panel, MonoLabel } from "@/components/Panel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authedImg = (url) => {
  // Backend photo variants require Bearer auth. We fetch as blob and hand a
  // blob: URL to <img>. Component below caches & revokes.
  return url; // placeholder — real usage goes through <AuthedImg />
};

function AuthedImg({ src, alt, className, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const t = localStorage.getItem(MEMBER_TOKEN_KEY);
        const res = await fetch(`${API}${src}`, { headers: { Authorization: `Bearer ${t}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const b = await res.blob();
        objectUrl = URL.createObjectURL(b);
        if (!cancelled) setBlobUrl(objectUrl);
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);
  if (err) return <div className={`${className} flex items-center justify-center text-xs`} style={{color:"var(--taupe)"}}>photo unavailable</div>;
  if (!blobUrl) return <div className={`${className} flex items-center justify-center`} style={{background:"var(--panel-2)"}}><Loader2 size={16} className="animate-spin" /></div>;
  return <img src={blobUrl} alt={alt} className={className} onClick={onClick} />;
}

export function MemberGalleryPanel() {
  const [state, setState] = useState({ loading: true, consent: null, selfie: null, count: 0 });
  const [photos, setPhotos] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const refresh = async () => {
    try {
      const consent = await galleryApi.getConsent();
      let selfie = { present: false }, count = { count: 0 };
      if (consent.consented) {
        [selfie, count] = await Promise.all([galleryApi.getSelfie(), galleryApi.count()]);
      }
      setState({ loading: false, consent, selfie, count: count.count });
      if (consent.consented && selfie.present) {
        const p = await galleryApi.myPhotos({ limit: 24 });
        setPhotos(p.items); setNextCursor(p.next_cursor);
      }
    } catch (e) { toast.error(galleryApiErr(e)); setState((s) => ({ ...s, loading: false })); }
  };

  useEffect(() => { refresh(); }, []);

  const grantConsent = async () => {
    setBusy(true);
    try { await galleryApi.grantConsent(); toast.success("Consent recorded"); await refresh(); }
    catch (e) { toast.error(galleryApiErr(e)); } finally { setBusy(false); }
  };
  const withdrawConsent = async () => {
    if (!window.confirm("Withdraw consent? Your face data will be deleted and you won't see event photos.")) return;
    setBusy(true);
    try { await galleryApi.withdrawConsent(); toast.success("Face data deleted"); setPhotos(null); await refresh(); }
    catch (e) { toast.error(galleryApiErr(e)); } finally { setBusy(false); }
  };
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await galleryApi.uploadSelfie(file);
      toast.success(`Enrolled · engine=${r.engine}`);
      await refresh();
    } catch (e) { toast.error(galleryApiErr(e)); } finally { setUploading(false); }
  };
  const deleteSelfie = async () => {
    if (!window.confirm("Delete your current selfie? You can upload a new one anytime.")) return;
    try { await galleryApi.deleteSelfie(); toast.success("Selfie removed"); await refresh(); }
    catch (e) { toast.error(galleryApiErr(e)); }
  };

  if (state.loading) return (
    <Panel className="panel-glass p-6"><MonoLabel>My Photos</MonoLabel>
      <div className="mt-6 flex items-center justify-center py-6" style={{color:"var(--taupe)"}}><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>
    </Panel>
  );

  // --- Step 1: consent gate ---
  if (!state.consent?.consented) return (
    <Panel className="panel-glass p-6" data-testid="gallery-consent-gate">
      <MonoLabel>My Photos · Consent Required</MonoLabel>
      <div className="mt-4 text-sm" style={{color:"var(--text)"}}>
        {state.consent?.consent_copy?.en}
      </div>
      <div className="mt-6 flex gap-3 flex-wrap">
        <button data-testid="gallery-consent-accept" className="btn-pill small" disabled={busy} onClick={grantConsent}>
          {busy ? "Working…" : "I consent"}
        </button>
        <button data-testid="gallery-consent-later" className="btn-pill small !bg-transparent" style={{ background: "transparent", color: "var(--text)" }} disabled={busy} onClick={() => toast("You can enable this later from Settings")}>
          Not now
        </button>
      </div>
      <div className="mono-label mt-4" style={{color:"var(--muted)"}}>DPDP Act 2023 · face data deletable anytime</div>
    </Panel>
  );

  // --- Step 2: selfie upload ---
  if (!state.selfie?.present) return (
    <Panel className="panel-glass p-6" data-testid="gallery-selfie-upload">
      <div className="flex items-center justify-between">
        <MonoLabel>My Photos · Enroll Selfie</MonoLabel>
        <button className="mono-label" style={{color:"var(--taupe)"}} onClick={withdrawConsent}><ShieldAlert size={12} className="inline mr-1" />Withdraw consent</button>
      </div>
      <div className="mt-4 text-sm" style={{color:"var(--taupe)"}}>
        Upload a clear selfie — we'll surface every event photo you appear in automatically.
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic"
        onChange={(e) => doUpload(e.target.files?.[0])}
        className="hidden" data-testid="gallery-selfie-file" />
      <button className="btn-red mt-5" disabled={uploading}
        onClick={() => fileRef.current?.click()}
        data-testid="gallery-selfie-pick">
        {uploading ? <><Loader2 size={14} className="animate-spin"/> Uploading…</> : <><Camera size={14}/> Take / choose selfie</>}
      </button>
      <div className="mono-label mt-3" style={{color:"var(--muted)"}}>JPG/PNG/WEBP · 500 KB – 10 MB · one face, well-lit</div>
    </Panel>
  );

  // --- Step 3: enrolled + grid ---
  const enrolled = state.selfie.enrollment_status === "enrolled";
  return (
    <Panel className="panel-glass p-6" data-testid="gallery-my-photos">
      <div className="flex items-center justify-between">
        <div>
          <MonoLabel>My Photos</MonoLabel>
          <div className="font-serif text-2xl mt-1">
            {state.count > 0 ? <>You appear in <span style={{color:"var(--red-2)"}}>{state.count}</span> photos</>
                              : "We're indexing event photos as they come in"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="mono-label" onClick={refresh} data-testid="gallery-refresh">
            <RefreshCw size={12} className="inline mr-1" />Refresh
          </button>
          <button className="mono-label" style={{color:"var(--taupe)"}} onClick={deleteSelfie} data-testid="gallery-retake">
            Retake selfie
          </button>
          <button className="mono-label" style={{color:"var(--red-2)"}} onClick={withdrawConsent}>
            <Trash2 size={12} className="inline mr-1" />Delete face data
          </button>
        </div>
      </div>

      {enrolled && (!photos || photos.length === 0) && (
        <div className="mt-6 hairline p-6 text-sm text-center" style={{color:"var(--taupe)"}}>
          <ImageIcon size={20} className="inline mr-2" />
          No matches yet — you'll get a WhatsApp when new photos of you are ready.
        </div>
      )}

      {photos && photos.length > 0 && (
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {photos.map((p) => (
            <button key={p.photo_id} onClick={() => setLightbox(p)} className="relative aspect-square overflow-hidden hairline group"
              data-testid={`gallery-photo-${p.photo_id}`}>
              <AuthedImg src={p.thumbnail_url} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
              <div className="absolute bottom-1 left-1 mono-label text-[10px] px-1 bg-black/60" style={{color:"#F7F4EE"}}>
                {Math.round(p.confidence || 0)}%
              </div>
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)}
             data-testid="gallery-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white"><X size={24} /></button>
          <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <AuthedImg src={lightbox.display_url} alt="" className="max-w-full max-h-[85vh] object-contain" />
            <a className="btn-red mt-3 inline-block" href="#" onClick={(e) => { e.preventDefault();
              const t = localStorage.getItem(MEMBER_TOKEN_KEY);
              fetch(`${API}${lightbox.display_url}`, { headers: { Authorization: `Bearer ${t}` } })
                .then(r => r.blob()).then(b => {
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(b);
                  a.download = `${lightbox.photo_id}.webp`;
                  a.click();
                });
            }}>Download</a>
          </div>
        </div>
      )}
    </Panel>
  );
}
